import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Building2,
  ChevronRight,
  GraduationCap,
  Lightbulb,
  Megaphone,
  Network,
  Pin,
  Plus,
  TrendingUp,
  UserPlus,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import type { Permission } from '@/constants/permissions';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { instituteNoticesApi } from '@/features/institute/notices/api/instituteNotices.api';
import { AUDIENCE_STYLE } from '@/features/institute/notices/constants';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { InstituteClass } from '@/types/instituteClass';
import { CLASS_STAGES, stageOfClass, type ClassStage } from '../utils';

/**
 * The three panels that close the classes screen.
 * Exact pixel-perfect match to the reference mockup design.
 */

/** The shell all three share, with top-right decorative matrix or custom action */
function Panel({
  icon: Icon,
  iconBadgeClass = 'bg-[#6366f1]/10 text-[#6366f1]',
  title,
  subtitle,
  action,
  children,
}: {
  icon: LucideIcon;
  iconBadgeClass?: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-100 dark:border-slate-800/80 p-5 shadow-sm dark:shadow-none flex flex-col justify-between h-full">
      <header className="flex items-center justify-between gap-3 mb-4 min-h-[44px]">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={cn('h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 shadow-2xs', iconBadgeClass)}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-extrabold tracking-normal text-slate-700 dark:text-white leading-tight">{title}</h2>
            {subtitle && <p className="text-[11.5px] font-medium text-slate-400 dark:text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action ? (
          action
        ) : (
          <div className="grid grid-cols-4 gap-1 opacity-20 dark:opacity-10 shrink-0 select-none mr-1" aria-hidden>
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} className="h-1 w-1 rounded-full bg-slate-500" />
            ))}
          </div>
        )}
      </header>

      <div className="flex-1 flex flex-col justify-start">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Quick actions
// ---------------------------------------------------------------------------

interface QuickAction {
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  badgeClass: string;
  to?: string;
  onClick?: () => void;
  needs?: Permission;
}

export interface QuickActionHandlers {
  onAddClass: () => void;
  onViewStructure: () => void;
}

function useQuickActions({
  onAddClass,
  onViewStructure,
}: QuickActionHandlers): QuickAction[] {
  const permissions = useAuthStore((state) => state.user?.permissions);

  const all: QuickAction[] = [
    {
      key: 'add',
      label: 'Add new class',
      description: 'Create a class with its sections',
      icon: Plus,
      badgeClass: 'bg-[#6366f1] text-white shadow-sm shadow-indigo-500/25',
      onClick: onAddClass,
      needs: 'CLASS_MANAGE',
    },
    {
      key: 'allot',
      label: 'Assign class teachers',
      description: 'Allot or change a section’s teacher',
      icon: UserPlus,
      badgeClass: 'bg-[#f59e0b] text-white shadow-sm shadow-amber-500/25',
      to: '/admin/staff',
      needs: 'STAFF_VIEW',
    },
    {
      key: 'structure',
      label: 'View academic structure',
      description: 'Explore the grade-wise breakdown',
      icon: Network,
      badgeClass: 'bg-[#3b82f6] text-white shadow-sm shadow-blue-500/25',
      onClick: onViewStructure,
    },
  ];

  return all.filter((action) => !action.needs || permissions?.includes(action.needs));
}

const ACTION_SHELL = cn(
  'group flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-[#0b1322] p-3 text-left shadow-2xs',
  'transition-all hover:bg-slate-50/80 dark:hover:bg-slate-800/40 hover:border-slate-200 dark:hover:border-slate-700 active:scale-[0.99]',
  'outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 cursor-pointer select-none',
);

function QuickActions({ actions }: { actions: QuickAction[] }) {
  return (
    <Panel
      icon={Zap}
      iconBadgeClass="bg-[#6366f1]/10 text-[#6366f1]"
      title="Quick actions"
      subtitle="Shortcuts to save your time"
    >
      <div className="flex flex-col gap-2.5">
        {actions.map((action) => {
          const Icon = action.icon;

          const body = (
            <>
              <span className="flex items-center gap-3 min-w-0 flex-1">
                <span className={cn('h-9 w-9 rounded-xl flex items-center justify-center shrink-0 shadow-xs', action.badgeClass)}>
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-bold text-slate-800 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                    {action.label}
                  </span>
                  <span className="mt-0.5 block truncate text-[11.5px] font-medium text-slate-400 dark:text-slate-500">
                    {action.description}
                  </span>
                </span>
              </span>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500 stroke-[2] shrink-0 ml-1" />
            </>
          );

          return action.to ? (
            <Link key={action.key} to={action.to} className={ACTION_SHELL}>
              {body}
            </Link>
          ) : (
            <button key={action.key} type="button" onClick={action.onClick} className={ACTION_SHELL}>
              {body}
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Academic summary
// ---------------------------------------------------------------------------

interface StageTotals {
  stage: ClassStage;
  classes: number;
  sections: number;
  students: number;
  span: string;
}

function totalsByStage(rows: InstituteClass[]): StageTotals[] {
  return CLASS_STAGES.flatMap((stage) => {
    const own = rows.filter((row) => stageOfClass(row) === stage);
    if (own.length === 0) return [];

    const first = own[0].name;
    const last = own[own.length - 1].name;

    return [
      {
        stage,
        classes: own.length,
        sections: own.reduce((sum, row) => sum + row.sections.length, 0),
        students: own.reduce((sum, row) => sum + row.studentCount, 0),
        span: own.length === 1 ? first : `${first} – ${last}`,
      },
    ];
  });
}

const STAGE_CONFIG: Record<
  string,
  {
    cardBg: string;
    iconCircle: string;
    statText: string;
  }
> = {
  'Pre-Primary': {
    cardBg: 'border-amber-100/80 dark:border-slate-800 bg-[#fffdf5] dark:bg-slate-900',
    iconCircle: 'bg-[#fef3c7] dark:bg-amber-500/15 text-[#d97706] dark:text-[#fbbf24]',
    statText: 'text-[#d97706] dark:text-[#fbbf24]',
  },
  Primary: {
    cardBg: 'border-indigo-100/80 dark:border-slate-800 bg-[#f8faff] dark:bg-slate-900',
    iconCircle: 'bg-[#e0e7ff] dark:bg-indigo-500/15 text-[#4f46e5] dark:text-[#818cf8]',
    statText: 'text-[#4f46e5] dark:text-[#818cf8]',
  },
  Middle: {
    cardBg: 'border-emerald-100/80 dark:border-slate-800 bg-[#f4fdf8] dark:bg-slate-900',
    iconCircle: 'bg-[#d1fae5] dark:bg-emerald-500/15 text-[#059669] dark:text-[#34d399]',
    statText: 'text-[#059669] dark:text-[#34d399]',
  },
  Secondary: {
    cardBg: 'border-sky-100/80 dark:border-slate-800 bg-[#f0f9ff] dark:bg-slate-900',
    iconCircle: 'bg-[#e0f2fe] dark:bg-sky-500/15 text-[#0284c7] dark:text-[#38bdf8]',
    statText: 'text-[#0284c7] dark:text-[#38bdf8]',
  },
  'Higher Secondary': {
    cardBg: 'border-purple-100/80 dark:border-slate-800 bg-[#faf5ff] dark:bg-slate-900',
    iconCircle: 'bg-[#f3e8ff] dark:bg-purple-500/15 text-[#9333ea] dark:text-[#c084fc]',
    statText: 'text-[#9333ea] dark:text-[#c084fc]',
  },
};

function AcademicSummary({
  rows,
  isLoading,
  onViewReport,
}: {
  rows: InstituteClass[];
  isLoading: boolean;
  onViewReport: () => void;
}) {
  const stages = totalsByStage(rows);
  const totalClasses = stages.reduce((sum, s) => sum + s.classes, 0);
  const totalSections = stages.reduce((sum, s) => sum + s.sections, 0);
  const totalStudents = stages.reduce((sum, s) => sum + s.students, 0);

  return (
    <Panel
      icon={BookOpen}
      iconBadgeClass="bg-[#6366f1]/10 text-[#6366f1]"
      title="Academic summary"
      action={
        <Button
          variant="secondary"
          size="sm"
          disabled={isLoading}
          className="!h-8 !py-1 !px-3 !text-[11.5px] font-bold border border-indigo-200/80 dark:border-indigo-800/80 bg-white dark:bg-[#0b1322] hover:bg-indigo-50/60 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-2xs cursor-pointer flex items-center gap-1.5"
          onClick={onViewReport}
        >
          <TrendingUp className="h-3.5 w-3.5" />
          <span>Open academic structure</span>
        </Button>
      }
    >
      {isLoading ? (
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-13 w-full rounded-2xl" />
          ))}
        </div>
      ) : stages.length === 0 ? (
        <p className="text-xs text-slate-400 py-4 text-center">Nothing to summarise yet. Add your first class and this fills in.</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {stages.map((row) => {
            const config = STAGE_CONFIG[row.stage] ?? STAGE_CONFIG.Primary;
            return (
              <li
                key={row.stage}
                className={cn('rounded-2xl border p-2.5 sm:p-3 flex items-center justify-between shadow-2xs gap-2', config.cardBg)}
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className={cn('h-7 w-7 sm:h-9 sm:w-9 rounded-full flex items-center justify-center shrink-0 shadow-2xs', config.iconCircle)}>
                    <GraduationCap className="h-3.5 w-3.5 sm:h-4.5 sm:w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs sm:text-[13px] font-black text-slate-800 dark:text-slate-100 leading-tight">
                      {row.stage}
                    </span>
                    <span className="block text-[10px] sm:text-[11px] font-semibold text-slate-400 truncate leading-tight mt-0.5">
                      ({row.span})
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3.5 shrink-0 pr-0.5 sm:pr-1">
                  <div className="flex flex-col items-center min-w-[28px] sm:min-w-[36px]">
                    <span className={cn('text-xs sm:text-[13.5px] font-black tabular-nums', config.statText)}>
                      {row.classes}
                    </span>
                    <span className="text-[9.5px] sm:text-[11px] font-bold text-slate-400 tracking-tight">
                      {row.classes === 1 ? 'Class' : 'Classes'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center min-w-[28px] sm:min-w-[36px]">
                    <span className={cn('text-xs sm:text-[13.5px] font-black tabular-nums', config.statText)}>
                      {row.sections}
                    </span>
                    <span className="text-[9.5px] sm:text-[11px] font-bold text-slate-400 tracking-tight">
                      {row.sections === 1 ? 'Section' : 'Sections'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center min-w-[28px] sm:min-w-[36px]">
                    <span className={cn('text-xs sm:text-[13.5px] font-black tabular-nums', config.statText)}>
                      {row.students}
                    </span>
                    <span className="text-[9.5px] sm:text-[11px] font-bold text-slate-400 tracking-tight">
                      {row.students === 1 ? 'Student' : 'Students'}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}

          {/* TOTAL INSTITUTIONAL SUMMARY (4th Row) */}
          <li className="rounded-2xl border border-purple-100/80 dark:border-purple-950/60 bg-[#faf5ff] dark:bg-[#200e33] p-2.5 sm:p-3 flex items-center justify-between shadow-2xs gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="h-7 w-7 sm:h-9 sm:w-9 rounded-lg sm:rounded-xl bg-[#7c3aed] text-white flex items-center justify-center shrink-0 shadow-sm shadow-purple-500/25">
                <Building2 className="h-3.5 w-3.5 sm:h-4.5 sm:w-4.5" />
              </div>
              <span className="text-xs sm:text-[13px] font-extrabold text-slate-800 dark:text-slate-100 leading-tight">
                Total across stages
              </span>
            </div>

            <div className="flex items-center gap-2 sm:gap-3.5 shrink-0 pr-0.5 sm:pr-1">
              <div className="flex flex-col items-center min-w-[28px] sm:min-w-[36px]">
                <span className="text-xs sm:text-[13.5px] font-black tabular-nums text-[#7c3aed] dark:text-[#a78bfa]">
                  {totalClasses}
                </span>
                <span className="text-[9.5px] sm:text-[11px] font-bold text-slate-400 tracking-tight">
                  Classes
                </span>
              </div>
              <div className="flex flex-col items-center min-w-[28px] sm:min-w-[36px]">
                <span className="text-xs sm:text-[13.5px] font-black tabular-nums text-[#7c3aed] dark:text-[#a78bfa]">
                  {totalSections}
                </span>
                <span className="text-[9.5px] sm:text-[11px] font-bold text-slate-400 tracking-tight">
                  Sections
                </span>
              </div>
              <div className="flex flex-col items-center min-w-[28px] sm:min-w-[36px]">
                <span className="text-xs sm:text-[13.5px] font-black tabular-nums text-[#7c3aed] dark:text-[#a78bfa]">
                  {totalStudents}
                </span>
                <span className="text-[9.5px] sm:text-[11px] font-bold text-slate-400 tracking-tight">
                  {totalStudents === 1 ? 'Student' : 'Students'}
                </span>
              </div>
            </div>
          </li>
        </ul>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Important notes
// ---------------------------------------------------------------------------

/** As many as the panel holds beside its two neighbours without scrolling. */
const NOTES_SHOWN = 4;

const NOTICE_BOARD_PATH = '/admin/notices';

/**
 * The newest notices from the institute's own board, pinned ones first.
 *
 * Read from the board rather than written here, so what the office puts up is
 * what this panel says — one place to change it, and no copy that is the same
 * for every institute. The server orders the board and decides which notices
 * this reader may see; the panel only takes the top of it.
 */
function ImportantNotes() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  // The board's own screen is behind this key, so the link is only offered to
  // somebody it would open for.
  const canOpenBoard = permissions?.includes('INSTITUTE_NOTICE_MANAGE') ?? false;
  const { data, isLoading, error, refetch } = useApiQuery(() => instituteNoticesApi.list(), []);

  const notices = (data?.notices ?? []).slice(0, NOTES_SHOWN);

  return (
    <Panel
      icon={Lightbulb}
      iconBadgeClass="bg-[#f59e0b] text-white shadow-sm shadow-amber-500/25 rounded-full"
      title="Important notes"
      subtitle="From your notice board"
      action={
        canOpenBoard ? (
          <Link
            to={NOTICE_BOARD_PATH}
            className="inline-flex items-center gap-0.5 rounded-lg px-2 py-1 text-[11.5px] font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            View all
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        ) : undefined
      }
    >
      {isLoading && !data && (
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: NOTES_SHOWN }, (_, index) => (
            <Skeleton key={index} className="h-14 w-full rounded-2xl" />
          ))}
        </div>
      )}

      {error && <QueryError title="Could not load the notice board" error={error} onRetry={refetch} />}

      {!isLoading && !error && notices.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 px-4 py-6 text-center">
          <Megaphone className="h-5 w-5 text-slate-400" aria-hidden />
          <p className="text-[12px] font-medium text-slate-500 dark:text-slate-400">
            Nothing on the notice board yet. Notices the school office puts up appear here.
          </p>
          {canOpenBoard && (
            <Link
              to={NOTICE_BOARD_PATH}
              className="text-[12px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"
            >
              Put up a notice
            </Link>
          )}
        </div>
      )}

      {!error && notices.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {notices.map((notice) => {
            const style = AUDIENCE_STYLE[notice.audience];
            const Icon = style.icon;

            return (
              <li
                key={notice.uuid}
                className="rounded-2xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-[#0b1322] p-3 flex items-center gap-3.5 shadow-2xs"
              >
                <div className={cn('h-9 w-9 rounded-xl border flex items-center justify-center shrink-0', style.chip)}>
                  <Icon className="h-4 w-4" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-[12.5px] font-bold text-slate-800 dark:text-slate-100 leading-snug">
                    {notice.isPinned && (
                      <Pin className="h-3 w-3 shrink-0 text-amber-500" aria-label="Pinned" />
                    )}
                    <span className="truncate" title={notice.title}>
                      {notice.title}
                    </span>
                  </p>
                  <p className="truncate text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    {formatDate(notice.noticeDate)} · {notice.audienceLabel}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

export interface ClassesInsightRowProps extends QuickActionHandlers {
  rows: InstituteClass[];
  isLoading: boolean;
  onViewReport: () => void;
}

export function ClassesInsightRow({
  rows,
  isLoading,
  onViewReport,
  onAddClass,
  onViewStructure,
}: ClassesInsightRowProps) {
  const actions = useQuickActions({ onAddClass, onViewStructure });
  const hasActions = actions.length > 0;

  return (
    <div className={cn('grid grid-cols-1 gap-6', hasActions ? 'lg:grid-cols-3' : 'lg:grid-cols-2')}>
      {hasActions && (
        <div className="h-full">
          <QuickActions actions={actions} />
        </div>
      )}

      <div className="h-full">
        <AcademicSummary rows={rows} isLoading={isLoading} onViewReport={onViewReport} />
      </div>

      <div className="h-full">
        <ImportantNotes />
      </div>
    </div>
  );
}
